import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import { useHotkeys } from "react-hotkeys-hook";
import AddressSidebar from "../../common-components/address-bar";
import SearchField from "../../forms/search-field";
import errorToast from "../../helper-components/error-toast";
import { customerListAction } from "../../../redux/actions/general-action";
import {
  getRowAllocations,
  getPoRowAllocationSummary,
} from "../../../utils/purchase-order-calculations";

// AddressSidebar reads/writes `${name}_ship_to_address` via a flat bracket
// lookup on Formik `values` (not a nested-path resolver), so it can't target
// `data[i].vendor_allocations[j]` directly — this scratch name bridges the
// two, synced back into the real allocation below. Index-derived (not built
// from allocation.key) so it can never contain characters setFieldValue
// would parse as a path.
const addrFieldName = (rowIndex, allocIndex) => `po_alloc_addr_r${rowIndex}_a${allocIndex}`;

// Same flat-lookup constraint applies to SearchField (reads formValues[name]
// directly) — one scratch field per search target ("add" or an allocation
// index), cleared back to null once a pick is consumed.
const vendorFieldName = (rowIndex, target) => `po_alloc_vendor_r${rowIndex}_${target}`;

const STATUS_LABEL = {
  not_allocated: "Not allocated",
  partial: "Partially allocated",
  full: "Fully allocated",
};
const STATUS_CLASS = {
  not_allocated: "bg-[var(--dashboard-gray-100)] text-[var(--dashboard-gray-600)]",
  partial: "bg-[var(--dashboard-bg-warning)] text-[var(--dashboard-warning-dark)]",
  full: "bg-[var(--status-badge-green-bg)] text-[var(--status-badge-green-fg)]",
};

const fieldLabelClass =
  "text-[10px] uppercase tracking-wide text-[var(--trans-stats-con-label-fg)] font-semibold";
const linkButtonClass =
  "text-[10px] font-semibold text-[var(--primary)] hover:underline whitespace-nowrap disabled:opacity-50 disabled:no-underline";

const AllocationCard = ({
  allocation,
  remaining,
  canRemove,
  onChangeQty,
  onRemove,
  onOpenAddressPicker,
  vendorSearchOpen,
  onToggleVendorSearch,
  vendorFieldName: fieldName,
  loadVendorOptions,
  onVendorChange,
}) => {
  const shipAddress = allocation.cmr_ship_to_address;
  const shipCity = shipAddress?.cmr_ship_address_city;
  const shipPincode = shipAddress?.cmr_ship_address_pincode;
  // A saved address without a city on file (data quality issue on that
  // vendor's record) shouldn't read as "no address selected" — fall back to
  // its name/street so a real save is never reported as if nothing happened.
  const shipLabel = shipAddress
    ? shipCity
      ? `Ship ${shipCity} ${shipPincode || ""}`
      : `Ship ${shipAddress?.cmr_ship_address_name || shipAddress?.cmr_ship_address_street || "address on file"}`
    : "No address selected";
  const maxQty = Number(allocation.qty || 0) + remaining;

  return (
    <div className="bg-[var(--create-table-row-bg)] border border-[var(--create-table-border)] border-l-4 border-l-[var(--primary)] rounded-md p-3 flex flex-col gap-2">
      <div className="flex justify-between items-start gap-3">
        <div>
          <div className="text-sm font-semibold text-[var(--heading-color)]">
            {allocation.cmr_name} {allocation.cmr_code ? `(${allocation.cmr_code})` : ""}
          </div>
          <div className="text-xs text-[var(--description-color)] whitespace-nowrap">
            {shipLabel}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <button type="button" onClick={onOpenAddressPicker} className={linkButtonClass}>
              Change Address
            </button>
            <button type="button" onClick={onToggleVendorSearch} className={linkButtonClass}>
              Change Vendor
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          title="Remove this vendor allocation"
          className="w-5 h-5 shrink-0 flex items-center justify-center text-[var(--description-color)] hover:text-[var(--heading-color)] disabled:opacity-30"
        >
          ✕
        </button>
      </div>

      {vendorSearchOpen && (
        <SearchField
          type="text"
          name={fieldName}
          label="Vendor"
          isRequired={false}
          isLabelReq={false}
          placeholder="Search Vendor..."
          cusType="Vendor"
          showCreateOption={false}
          loadOptions={loadVendorOptions}
          onChange={onVendorChange}
        />
      )}

      <div>
        <span className={fieldLabelClass}>Qty</span>
        <div className="flex items-center border border-[var(--input-border)] rounded-md overflow-hidden mt-1 max-w-[10rem]">
          <button
            type="button"
            disabled={Number(allocation.qty) <= 0}
            onClick={() => onChangeQty(Number(allocation.qty) - 1)}
            className="w-7 h-8 shrink-0 flex items-center justify-center text-[var(--nav-heading-color)] hover:bg-[var(--row-hover-color)] disabled:opacity-30"
          >
            −
          </button>
          <input
            type="number"
            min={0}
            max={maxQty}
            value={allocation.qty}
            onChange={(e) => onChangeQty(e.target.value)}
            className="w-full text-center text-sm font-semibold border-x border-[var(--input-border)] py-1.5 focus:outline-none"
          />
          <button
            type="button"
            disabled={Number(allocation.qty) >= maxQty}
            onClick={() => onChangeQty(Number(allocation.qty) + 1)}
            className="w-7 h-8 shrink-0 flex items-center justify-center text-[var(--nav-heading-color)] hover:bg-[var(--row-hover-color)] disabled:opacity-30"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

const BulkPoAllocateSidePanel = ({ open, setOpen, rowIndex, preferenceLabel }) => {
  const close = useRef(null);
  const dispatch = useDispatch();
  const { values, setFieldValue } = useFormikContext();
  const row = rowIndex != null ? values?.data?.[rowIndex] : null;
  const basePath = `data[${rowIndex}].vendor_allocations`;
  // Renders (and every update) derive from getRowAllocations so the panel
  // works identically whether vendor_allocations already exists in Formik
  // state or the row still only has its original flat vendor fields — the
  // implicit single allocation is materialized into real state the first
  // time it's actually edited, not eagerly on open.
  const allocations = getRowAllocations(row);

  // null = closed, "add" = the panel-level Add Vendor box, a number = the
  // Change Vendor box for that allocation index. Only one open at a time.
  const [openVendorSearchFor, setOpenVendorSearchFor] = useState(null);
  const [vendorLocalData, setVendorLocalData] = useState([]);

  const [addressPickerIndex, setAddressPickerIndex] = useState(null);
  // Survives handleCloseAddressPicker (which only clears the visual open
  // state) so the sync effect below can still observe AddressSidebar's Save
  // writing into Formik in the same handler that closes it — same race as
  // bulk-allocate-side-panel.js and the same fix.
  const [addressSyncTargetIndex, setAddressSyncTargetIndex] = useState(null);

  useEffect(() => {
    if (!open) {
      setOpenVendorSearchFor(null);
      setAddressPickerIndex(null);
      setAddressSyncTargetIndex(null);
    }
  }, [open]);

  useHotkeys("esc", () => {
    // While the address picker is open, let it handle its own Escape close
    // instead of also closing this panel underneath it.
    if (addressPickerIndex != null) return;
    if (close.current) close.current.click();
  });

  const writeAllocations = (next) => {
    setFieldValue(basePath, next);
    // Keep the main table's Vendor Code/Name cells (and BulkOrderfilterFn's
    // vendor grouping/filter, which read the flat fields) coherent for the
    // common single-vendor case.
    if (next.length === 1) {
      setFieldValue(`data[${rowIndex}].cmr_id`, next[0].cmr_id);
      setFieldValue(`data[${rowIndex}].cmr_code`, next[0].cmr_code);
      setFieldValue(`data[${rowIndex}].cmr_first_name`, next[0].cmr_name);
    }
  };

  const updateAllocation = (i, changes) =>
    writeAllocations(allocations.map((a, j) => (j === i ? { ...a, ...changes } : a)));

  const removeAllocation = (i) => {
    if (allocations.length <= 1) return;
    writeAllocations(allocations.filter((_, j) => j !== i));
  };

  const handleOpenAddressPicker = (i) => {
    const prefix = addrFieldName(rowIndex, i);
    setFieldValue(`${prefix}_ship_to_address`, allocations[i]?.cmr_ship_to_address, false);
    setFieldValue(`${prefix}_pay_to_address`, allocations[i]?.cmr_pay_to_address || null, false);
    setAddressPickerIndex(i);
    setAddressSyncTargetIndex(i);
  };
  const handleCloseAddressPicker = () => setAddressPickerIndex(null);

  const syncPrefix = addressSyncTargetIndex != null ? addrFieldName(rowIndex, addressSyncTargetIndex) : null;
  const syncShipValue = syncPrefix ? values[`${syncPrefix}_ship_to_address`] : null;
  const syncPayValue = syncPrefix ? values[`${syncPrefix}_pay_to_address`] : null;

  useEffect(() => {
    if (addressSyncTargetIndex != null && syncShipValue) {
      updateAllocation(addressSyncTargetIndex, { cmr_ship_to_address: syncShipValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncShipValue]);

  // The picker saves pay-to and ship-to together (AddressSidebar.onSave) —
  // the PO create payload needs both (pot_pay_to_address/pot_ship_to_address),
  // so the pay-to side gets the same sync treatment as ship-to above.
  useEffect(() => {
    if (addressSyncTargetIndex != null && syncPayValue) {
      updateAllocation(addressSyncTargetIndex, { cmr_pay_to_address: syncPayValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncPayValue]);

  const activeAlloc = addressPickerIndex != null ? allocations[addressPickerIndex] : null;

  const summary = getPoRowAllocationSummary(row || {});
  const progressPct = summary.required > 0 ? Math.min((summary.allocated / summary.required) * 100, 100) : 0;

  // Blocks every way out of the panel (✕, Close, overlay click, Escape) once
  // an allocation is partially in progress — a row with nothing allocated
  // yet is fine to leave for later, but a half-finished split (some qty
  // already assigned, some still unaccounted for) must be completed first,
  // otherwise it'd silently leave the row under-allocated.
  const handleClose = () => {
    if (summary.status === "partial") {
      errorToast(
        `Allocate the remaining ${summary.remaining} before closing`
      );
      return;
    }
    setOpen(false);
  };

  const addAllocation = ({ cmr_id, cmr_code, cmr_name }) => {
    if (!cmr_id) return;
    if (allocations.some((a) => a.cmr_id === cmr_id)) {
      errorToast("This vendor is already allocated to this item");
      return;
    }
    writeAllocations([
      ...allocations,
      {
        key: `alloc-${cmr_id}-${allocations.length}`,
        cmr_id,
        cmr_code,
        cmr_name,
        cmr_ship_to_address: null,
        qty: Math.max(0, summary.remaining),
      },
    ]);
  };

  // Real vendor lookup — same endpoint/pattern as the rest of the Purchase
  // Order module (bulk-billing-table.js's vendor cell, sales-purchase-navbar.js).
  const loadVendorOptions = (inputValue) =>
    new Promise((resolve) => {
      if (!inputValue?.length) {
        resolve([]);
        return;
      }
      dispatch(
        customerListAction(
          { field: "cmr_first_name", value: inputValue, isVendor: true },
          (data) => {
            const items = data?.data || [];
            setVendorLocalData(items);
            resolve(
              items.map((item) => ({
                value: item.cmr_id,
                label: `${item.cmr_code}-${item.cmr_first_name}`,
              }))
            );
          },
          () => resolve([])
        )
      );
    });

  const handleVendorChange = (target) => (selectedOption, actionMeta) => {
    if (!selectedOption || actionMeta?.action === "clear") return;
    const picked = vendorLocalData.find((item) => item.cmr_id === selectedOption.value);
    if (!picked) return;

    if (target === "add") {
      addAllocation({ cmr_id: picked.cmr_id, cmr_code: picked.cmr_code, cmr_name: picked.cmr_first_name });
    } else if (allocations.some((a, idx) => idx !== target && a.cmr_id === picked.cmr_id)) {
      errorToast("This vendor is already allocated to this item");
    } else {
      // A new vendor invalidates whatever address was picked for the old one -
      // both sides; the pay-to drives the PO's GSTIN and Inter/Intra state.
      updateAllocation(target, {
        cmr_id: picked.cmr_id,
        cmr_code: picked.cmr_code,
        cmr_name: picked.cmr_first_name,
        cmr_ship_to_address: null,
        cmr_pay_to_address: null,
      });
    }
    setFieldValue(vendorFieldName(rowIndex, target), null, false);
    setOpenVendorSearchFor(null);
  };

  const showPreferenceQuickPick =
    openVendorSearchFor === "add" && row?.cmr_id && !allocations.some((a) => a.cmr_id === row.cmr_id);

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            // AddressSidebar is a sibling Headless UI Dialog, not a
            // descendant — without this guard, a click inside it registers
            // as "outside" this panel and closes both at once.
            if (addressPickerIndex != null) return;
            handleClose();
          }}
        >
          <div className="fixed inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
            </Transition.Child>

            <div className="fixed inset-0 flex justify-end">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="h-full relative w-[400px] sm:w-[640px] bg-[var(--create-table-row-bg)] rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                  {/* Header */}
                  <div className="p-4 flex justify-between items-start bg-[var(--create-table-header-bg)]">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-[var(--create-table-header-fg)] font-semibold">
                        Allocate Vendors
                      </div>
                      <div className="text-base sm:text-lg font-bold text-[var(--create-table-header-fg)] leading-snug">
                        {row?.item_name}
                      </div>
                    </div>
                    <button
                      type="button"
                      ref={close}
                      onClick={handleClose}
                      className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-white/10 text-[var(--create-table-header-fg)] hover:bg-white/20"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Stats bar */}
                  <div className="px-4 py-3 bg-[var(--slide-panel-head-bg)] border-b border-[var(--slide-panel-border)] flex flex-col gap-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-6">
                        <div>
                          <div className={fieldLabelClass}>Required</div>
                          <div className="text-2xl font-bold text-[var(--trans-stats-con-value-fg)]">
                            {summary.required}
                          </div>
                        </div>
                        <div>
                          <div className={fieldLabelClass}>Allocated</div>
                          <div className="text-2xl font-bold text-[var(--trans-stats-con-orange-fg)]">
                            {summary.allocated}
                          </div>
                        </div>
                        <div>
                          <div className={fieldLabelClass}>Remaining</div>
                          <div className="text-2xl font-bold text-[var(--trans-stats-con-orange-fg)]">
                            {summary.remaining}
                          </div>
                        </div>
                      </div>
                      <span
                        className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${STATUS_CLASS[summary.status]}`}
                      >
                        {STATUS_LABEL[summary.status]}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[var(--gray-200)] overflow-hidden">
                      <div
                        className="h-full bg-[var(--trans-stats-con-orange-fg)] transition-all"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                  </div>

                  {/* Scrollable content */}
                  <div className="space-y-3 p-4 flex-1 overflow-y-auto">
                    {allocations.length === 0 ? (
                      <div className="text-sm text-[var(--description-color)] text-center py-8">
                        No vendor allocated yet.
                      </div>
                    ) : (
                      allocations.map((allocation, index) => (
                        <AllocationCard
                          key={allocation.key || index}
                          allocation={allocation}
                          remaining={summary.remaining}
                          canRemove={allocations.length > 1}
                          onChangeQty={(qty) =>
                            updateAllocation(index, {
                              qty: Math.max(
                                0,
                                Math.min(Number(qty) || 0, Number(allocation.qty) + summary.remaining)
                              ),
                            })
                          }
                          onRemove={() => removeAllocation(index)}
                          onOpenAddressPicker={() => handleOpenAddressPicker(index)}
                          vendorSearchOpen={openVendorSearchFor === index}
                          onToggleVendorSearch={() =>
                            setOpenVendorSearchFor(openVendorSearchFor === index ? null : index)
                          }
                          vendorFieldName={vendorFieldName(rowIndex, index)}
                          loadVendorOptions={loadVendorOptions}
                          onVendorChange={handleVendorChange(index)}
                        />
                      ))
                    )}

                    <div className="border-t border-[var(--create-table-border)] pt-3 flex flex-col gap-2">
                      {openVendorSearchFor !== "add" ? (
                        <button
                          type="button"
                          onClick={() => setOpenVendorSearchFor("add")}
                          className="self-start text-xs font-semibold text-[var(--primary)] hover:underline"
                        >
                          + Add Vendor
                        </button>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <span className={fieldLabelClass}>Add Vendor</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFieldValue(vendorFieldName(rowIndex, "add"), null, false);
                                setOpenVendorSearchFor(null);
                              }}
                              className="w-5 h-5 shrink-0 flex items-center justify-center text-[var(--description-color)] hover:text-[var(--heading-color)]"
                            >
                              ✕
                            </button>
                          </div>
                          {showPreferenceQuickPick && (
                            <button
                              type="button"
                              onClick={() =>
                                addAllocation({
                                  cmr_id: row.cmr_id,
                                  cmr_code: row.cmr_code,
                                  cmr_name: row.cmr_first_name,
                                })
                              }
                              className="text-left text-xs font-semibold text-[var(--primary)] hover:underline"
                            >
                              + {preferenceLabel}: {row.cmr_first_name}{" "}
                              {row.cmr_code ? `(${row.cmr_code})` : ""}
                            </button>
                          )}
                          <SearchField
                            type="text"
                            name={vendorFieldName(rowIndex, "add")}
                            label="Vendor"
                            isRequired={false}
                            isLabelReq={false}
                            placeholder="Search Vendor..."
                            cusType="Vendor"
                            showCreateOption={false}
                            loadOptions={loadVendorOptions}
                            onChange={handleVendorChange("add")}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="p-4 border-t border-[var(--slide-panel-border)] bg-[var(--create-table-row-bg)] sticky bottom-0 flex justify-end">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-6 py-2 rounded-md fw_600 bg-[var(--primary)] text-white hover:opacity-90"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {activeAlloc && (
        <AddressSidebar
          type="vendor"
          page="add"
          disabled={false}
          path=""
          open={addressPickerIndex != null}
          setOpen={(isOpen) => {
            if (!isOpen) handleCloseAddressPicker();
          }}
          id={activeAlloc.cmr_id}
          name={addrFieldName(rowIndex, addressPickerIndex)}
          // Writes straight into the allocation the instant Save fires,
          // instead of relying on the scratch-field + effect roundtrip below
          // (kept as a harmless backup) to eventually pick it up.
          onSaved={({ payData, shipData }) => {
            const changes = {};
            if (shipData) changes.cmr_ship_to_address = shipData;
            if (payData) changes.cmr_pay_to_address = payData;
            if (Object.keys(changes).length > 0) {
              updateAllocation(addressPickerIndex, changes);
            }
          }}
          handleDraft={() => errorToast("Add a new address from the vendor's profile")}
        />
      )}
    </>
  );
};

export default BulkPoAllocateSidePanel;
